import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useStyles } from './styles';
import { AuthContext } from '../../shared/app/AuthProvider';
import AuthorizedLayout from '../../shared/layout/AuthorizedLayout';
import DownloadExport from './components/DownloadExport';
import ModalDeleteAccount from '../../shared/modal/ModalDeleteAccount';
import ModalExportData from '../../shared/modal/ModalExportData';
import { ProfileGridItem } from './components/ProfileGridItem';
import { BookingHistory } from './components/BookingHistory';
import { patchUser, selectUser, useAppDispatch } from '../../../store';
import { addToastInfo } from '../../../store/common';
import { ProfileActionType } from '../../../types';
import { useAppConfiguration } from '../../shared/hook';
import { ModalWithConfirmation } from '../../shared/modal/ModalWithConfirmation';

export const Profile = () => {
  const [deleteAccountModal, setDeleteAccountModal] = useState({
    isOpen: false,
  });
  const [unsubscribeModal, setUnsubscribeModal] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [exportDataModal, setExportDataModal] = useState({ isOpen: false });
  const { authUser } = useContext(AuthContext);
  const { t } = useTranslation();
  const { user } = useSelector(selectUser);
  const { withUnlimitedCredits } = useAppConfiguration();
  const classes = useStyles({ withUnlimitedCredits });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleConsent = (consents: Record<string, boolean>) => {
    dispatch(patchUser(consents));

    const hasChecked = Object.values(consents).some(Boolean);

    if (hasChecked) {
      dispatch(
        addToastInfo({
          toastInfo: {
            open: true,
            type: 'success',
            content: t(
              'v2.global.snackbar_label_getNewsletterUpdates_subscribed',
            ),
          },
        }),
      );
    } else {
      dispatch(
        addToastInfo({
          toastInfo: {
            open: true,
            type: 'warningInfo',
            content: t(
              'v2.account.profile_getNewsletterUpdates_label_snackbar_deactivated',
            ),
          },
        }),
      );
    }
  };

  const handleToggleConsents = (checked: boolean) => {
    if (!checked) {
      setUnsubscribeModal(true);
      return;
    }

    handleConsent({
      recommendationsConsent: checked,
      marketingConsent: checked,
    });
  };

  const handleUnsubscribeAll = () => {
    handleConsent({
      recommendationsConsent: false,
      marketingConsent: false,
    });
    setUnsubscribeModal(false);
  };

  const personalInformationLabelValues = [
    {
      label: t('Profile.name'),
      value: authUser.attributes.given_name,
    },
    {
      label: t('Profile.email'),
      value: authUser.attributes.email,
      type: 'email',
      className: classes.truncate,
    },
    {
      label: t('v2.account.profile_getNewsletterUpdates_label_title'),
      value: t('v2.account.profile_getNewsletterUpdates_label_body'),
      type: 'email',
      checked: user?.marketingConsent,
      actionToggle: 'toggle',
      onToggle: (checked: boolean) =>
        handleConsent({ marketingConsent: checked }),
    },
    {
      value: t(
        'v2.account.profile_getNewsletterUpdates_label_recommendationsEmails',
      ),
      type: 'email',
      checked: user?.recommendationsConsent,
      actionToggle: 'toggle',
      onToggle: (checked: boolean) =>
        handleConsent({ recommendationsConsent: checked }),
      hasDivider: true,
    },
    {
      value: t('v2.account.profile_getNewsletterUpdates_label_subscribeToAll'),
      type: 'email',
      checked: user?.marketingConsent && user?.recommendationsConsent,
      actionToggle: 'toggle',
      onToggle: handleToggleConsents,
    },
  ];

  const walletLabelValues = [
    {
      label: t('v2.account.wallet_card_label_availableCredits'),
      value: user?.creditBalance,
      actionLabel: t('v2.account.wallet_card_button_getMoreCredits'),
      actionHandler: ProfileActionType.REQUEST_CREDIT,
    },
    {
      label: t('v2.account.wallet_card_label_renewalDate'),
      value: user?.subscription?.end,
    },
  ];

  const handleCloseUnsubscribeModal = () => setUnsubscribeModal(false);

  return (
    <AuthorizedLayout title={t('Profile.account')}>
      <Box className={classes.mainContainer}>
        <Box className={classes.contentContainer}>
          <Typography variant="h2">{t('Profile.account')}</Typography>
          <Box className={classes.accountSettingsBox}>
            <Box
              display="flex"
              flexDirection="column"
              flex={1}
              className={classes.itemWrapper}
            >
              <ProfileGridItem
                title={t('Profile.profileInformation')}
                classes={classes}
                labelValues={personalInformationLabelValues}
              />
            </Box>
            {!withUnlimitedCredits && (
              <Box
                display="flex"
                flexDirection="column"
                flex={1}
                className={classes.itemWrapper}
              >
                <ProfileGridItem
                  title={t('v2.account.wallet_card_label_title')}
                  classes={classes}
                  labelValues={walletLabelValues}
                  infoText={t('v2.account.wallet_banner_label_renewalInfo')}
                />
              </Box>
            )}
          </Box>
          <Box className={classes.tableContainer}>
            <BookingHistory />
          </Box>
          <Box className={classes.actionsContainer}>
            <DownloadExport
              onExportAction={() => setExportDataModal({ isOpen: true })}
              onDownloadAction={() => setDeleteAccountModal({ isOpen: true })}
            />
          </Box>
          <ModalExportData
            modal={exportDataModal}
            onClose={() => setExportDataModal({ isOpen: false })}
          />
          <ModalDeleteAccount
            modal={deleteAccountModal}
            onClose={() => setDeleteAccountModal({ isOpen: false })}
          />
          <ModalWithConfirmation
            onClose={handleCloseUnsubscribeModal}
            onCancel={handleUnsubscribeAll}
            isOpen={unsubscribeModal}
            onConfirm={handleCloseUnsubscribeModal}
            title={t(
              'v2.account.profile_getNewsletterUpdates_modal_unsubscribe_label_title',
            )}
            text={t(
              'v2.account.profile_getNewsletterUpdates_modal_unsubscribe_label_body',
            )}
            confirmBtnLabel={t('v2.global.button_label_goBack')}
            cancelBtnlabel={t(
              'v2.account.profile_getNewsletterUpdates_modal_unsubscribe_label_button',
            )}
          />
        </Box>
      </Box>
    </AuthorizedLayout>
  );
};
